import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Button, Callout, Input, Modal, Select, Spinner } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useToast } from '@/shared/ui/Toast';
import { skillLabel } from '@/modules/agents/components/labels';
import type { AgentIdentity } from '@/modules/agents/utils/agentLabels';
import { agencyApi } from '../../api/agencyApi';
import { overridesApi, type SkillOverrideRow } from '../../api/overridesApi';
import { registryApi, type SkillChange } from '../../api/registryApi';
import { overrideErrorMessage } from './overrideErrorMessage';
import { agencyKeys, overrideKeys, registryKeys } from '../../queryKeys';
import { diffLines, diffStats, stripFrontmatter } from '../../utils/textDiff';
import styles from '../../pages/AgencySkillOverridesPage.module.css';

/**
 * The three skill-override dialogs, shared by the Practice Overrides page and the practice's Agents
 * tab: the diff against the global skill, the promote-to-global pull request, and the editor for a
 * new override. Each takes an override row that already knows its practice and location.
 */

const DAY = 86_400_000;

export type OverrideRow = SkillOverrideRow & { id: string };

// ─── Diff ─────────────────────────────────────────────────────────────────────

function DiffBlock({ title, before, after }: { title: string; before: string; after: string }) {
  const ops = useMemo(() => diffLines(before, after), [before, after]);
  const stats = diffStats(ops);
  if (stats.added === 0 && stats.removed === 0) return null;
  return (
    <div className={styles.diffSection}>
      <div className={styles.diffStats}>
        {title}: +{stats.added} −{stats.removed}
      </div>
      <pre className={styles.diff}>
        {ops.map((op, i) => (
          <span
            key={i}
            className={clsx(
              styles.diffLine,
              op.type === 'add' && styles.diffAdd,
              op.type === 'remove' && styles.diffRemove,
              op.type === 'same' && styles.diffSame,
            )}
          >
            {op.type === 'add' ? '+ ' : op.type === 'remove' ? '− ' : '  '}
            {op.text}
          </span>
        ))}
      </pre>
    </div>
  );
}

export function DiffModal({ row, onClose }: { row: OverrideRow | null; onClose: () => void }) {
  const files = useQuery({
    queryKey: overrideKeys.files(row?.practice.id ?? '', row?.location.id ?? '', row?.id ?? ''),
    queryFn: () => overridesApi.files(row!.practice.id, row!.location.id, row!.id),
    enabled: !!row,
  });
  const paths = files.data
    ? [...new Set([...Object.keys(files.data.global), ...Object.keys(files.data.effective)])].sort()
    : [];
  return (
    <Modal
      isOpen={!!row}
      onClose={onClose}
      title={row ? `${skillLabel(row.skillKey)} — override vs global` : 'Diff'}
      size="xl"
    >
      {files.isLoading && <Spinner />}
      {files.isError && (
        <Callout variant="error" title="Could not load the files">
          {overrideErrorMessage(files.error)}
        </Callout>
      )}
      {files.data && (
        <>
          <p className={patterns.cellMuted}>
            {row?.practice.name} · {row?.location.name} · {row?.reason}
          </p>
          {paths.map((p) => (
            <DiffBlock
              key={p}
              title={p}
              before={files.data.global[p] ?? ''}
              after={files.data.effective[p] ?? ''}
            />
          ))}
          {paths.every((p) => (files.data.global[p] ?? '') === (files.data.effective[p] ?? '')) && (
            <p className={styles.empty}>The override is identical to the global skill.</p>
          )}
        </>
      )}
    </Modal>
  );
}

// ─── Promote ──────────────────────────────────────────────────────────────────

export function PromoteModal({
  row,
  onClose,
  onDone,
}: {
  row: OverrideRow | null;
  onClose: () => void;
  onDone: (change: SkillChange) => void;
}) {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  useEffect(() => {
    if (row) {
      setTitle(`Promote ${row.practice.name}'s ${skillLabel(row.skillKey)} override`);
      setSummary('');
    }
  }, [row]);
  const promote = useMutation({
    mutationFn: () =>
      overridesApi.promote(row!.practice.id, row!.location.id, row!.id, {
        title: title.trim(),
        summary: summary.trim() || undefined,
      }),
    onSuccess: (change) => onDone(change),
  });
  return (
    <Modal isOpen={!!row} onClose={onClose} title="Promote to the global skill" size="md">
      <div className={styles.form}>
        <p className={patterns.cellMuted}>
          Opens a pull request that applies this override to the global skill for every practice.
          The override keeps applying here until you retire it — usually after the pull request
          merges.
        </p>
        <Input
          label="Pull request title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div>
          <label className={patterns.fieldLabel} htmlFor="promote-summary">
            Why this should be global
          </label>
          <textarea
            id="promote-summary"
            className={patterns.textarea}
            rows={3}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
        </div>
        {promote.isError && (
          <Callout variant="error" title="Could not open the pull request">
            {overrideErrorMessage(promote.error)}
          </Callout>
        )}
        <div className={styles.footer}>
          <Button
            size="sm"
            disabled={title.trim().length < 3 || promote.isPending}
            onClick={() => promote.mutate()}
          >
            {promote.isPending ? 'Opening…' : 'Open pull request'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── New override ─────────────────────────────────────────────────────────────

export function NewOverrideModal({
  open,
  onClose,
  agents,
  defaultPracticeId,
  defaultLocationId,
  defaultAgentType,
}: {
  open: boolean;
  onClose: () => void;
  agents: AgentIdentity[];
  /** Pre-fill the practice when the dialog opens from a practice-scoped surface. */
  defaultPracticeId?: string;
  /** Pre-fill the location; only meaningful together with `defaultPracticeId`. */
  defaultLocationId?: string;
  /** Offer only the skills of this agent (a role key) when the dialog opens for one agent. */
  defaultAgentType?: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [practiceId, setPracticeId] = useState(defaultPracticeId ?? '');
  const [locationId, setLocationId] = useState(defaultLocationId ?? '');
  const [skillKey, setSkillKey] = useState('');
  const [method, setMethod] = useState('');
  const [objective, setObjective] = useState('');
  const [references, setReferences] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('30');
  const [loadedFrom, setLoadedFrom] = useState<{
    method: string;
    objective: string;
    references: Record<string, string>;
  } | null>(null);

  // A pre-filled dialog re-seeds its scope each time it opens: the Agents tab keeps one dialog
  // mounted while the operator switches between agents and locations.
  useEffect(() => {
    if (!open || defaultPracticeId === undefined) return;
    setPracticeId(defaultPracticeId);
    setLocationId(defaultLocationId ?? '');
    setSkillKey('');
    setLoadedFrom(null);
  }, [open, defaultPracticeId, defaultLocationId]);

  const practices = useQuery({
    queryKey: agencyKeys.practices({ limit: 200 }),
    queryFn: () => agencyApi.getPractices({ limit: 200 }),
    enabled: open,
  });
  const practice = useQuery({
    queryKey: agencyKeys.practice(practiceId),
    queryFn: () => agencyApi.getPractice(practiceId),
    enabled: open && !!practiceId,
  });
  const registry = useQuery({
    queryKey: registryKeys.registry(),
    queryFn: () => registryApi.getRegistry(),
    enabled: open,
  });
  const files = useQuery({
    queryKey: registryKeys.files(skillKey),
    queryFn: () => registryApi.getSkillFiles(skillKey),
    enabled: open && !!skillKey,
  });

  const practiceLocations = practice.data?.data?.locations;
  const locations = useMemo(() => practiceLocations ?? [], [practiceLocations]);
  useEffect(() => {
    if (locations.length > 0 && !locations.some((l) => l.id === locationId))
      setLocationId(locations[0]!.id);
  }, [locations, locationId]);

  // Seed the editor from the global skill so an operator edits, rather than writes from scratch.
  useEffect(() => {
    if (!files.data) return;
    const f = files.data.files;
    const methodText = stripFrontmatter(f['method.md'] ?? f['SKILL.md'] ?? '');
    let objectiveText = '';
    try {
      objectiveText = String(
        (JSON.parse(f['manifest.json'] ?? '{}') as { objective?: string }).objective ?? '',
      );
    } catch {
      objectiveText = '';
    }
    const refs: Record<string, string> = {};
    for (const [p, text] of Object.entries(f))
      if (p.startsWith('references/') && p.endsWith('.md'))
        refs[p.slice('references/'.length, -3)] = text;
    setMethod(methodText);
    setObjective(objectiveText);
    setReferences(refs);
    setLoadedFrom({ method: methodText, objective: objectiveText, references: refs });
  }, [files.data]);

  const changedMethod = loadedFrom ? method !== loadedFrom.method : false;
  const changedObjective = loadedFrom ? objective !== loadedFrom.objective : false;
  const changedRefs = loadedFrom
    ? JSON.stringify(references) !== JSON.stringify(loadedFrom.references)
    : false;
  const ready =
    !!practiceId &&
    !!locationId &&
    !!skillKey &&
    reason.trim().length >= 3 &&
    (changedMethod || changedObjective || changedRefs);

  const create = useMutation({
    mutationFn: () => {
      const n = Number(days);
      return overridesApi.create(practiceId, locationId, {
        skillKey,
        reason: reason.trim(),
        ...(changedMethod ? { method } : {}),
        ...(changedObjective ? { objective } : {}),
        ...(changedRefs ? { references } : {}),
        ...(Number.isFinite(n) && n > 0 ? { expiresAt: Date.now() + n * DAY } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: overrideKeys.all });
      toast.success('Override created. It applies on the next run for that location.');
      onClose();
    },
    onError: (err) => toast.error(overrideErrorMessage(err)),
  });

  const liveSkills = (registry.data?.skills ?? []).filter(
    (s) =>
      s.status === 'live' &&
      s.methodFormat !== 'none' &&
      (!defaultAgentType || s.agentType === defaultAgentType),
  );

  return (
    <Modal isOpen={open} onClose={onClose} title="New practice override" size="xl">
      <div className={styles.form}>
        <Callout variant="warning" title="Overrides are the exception">
          A practice-specific version of one skill&apos;s methodology, time-boxed by default. Tools,
          mode and schedule stay global — only what the agent reads changes.
        </Callout>
        <div className={styles.fields}>
          <Select
            label="Practice"
            value={practiceId}
            placeholder="Choose a practice"
            options={(practices.data?.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            onChange={(e) => {
              setPracticeId(e.target.value);
              setLocationId('');
            }}
          />
          <Select
            label="Location"
            value={locationId}
            placeholder={practiceId ? 'Choose a location' : 'Pick a practice first'}
            options={locations.map((l) => ({ value: l.id, label: l.name }))}
            onChange={(e) => setLocationId(e.target.value)}
          />
          <div className={styles.fieldsWide}>
            <Select
              label="Skill"
              value={skillKey}
              placeholder="Choose a skill"
              options={liveSkills.map((s) => ({
                value: s.key,
                label: `${skillLabel(s.key)} · ${agents.find((a) => a.roleKey === s.agentType)?.personaName ?? s.agentType}`,
              }))}
              onChange={(e) => setSkillKey(e.target.value)}
            />
          </div>
        </div>

        {files.isLoading && <Spinner />}
        {files.data && (
          <>
            <div>
              <div className={styles.editorLabel}>
                <span>Objective</span>
                <span className={styles.editorHint}>
                  {changedObjective ? 'changed' : 'same as global'}
                </span>
              </div>
              <textarea
                className={clsx(styles.editor, styles.editorShort)}
                aria-label="Objective"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
              />
            </div>
            <div>
              <div className={styles.editorLabel}>
                <span>Method</span>
                <span className={styles.editorHint}>
                  {changedMethod ? 'changed' : 'same as global'} · frontmatter is kept from the
                  global file
                </span>
              </div>
              <textarea
                className={styles.editor}
                aria-label="Method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              />
            </div>
            {Object.keys(references).length > 0 && (
              <div>
                <div className={styles.editorLabel}>
                  <span>References</span>
                  <span className={styles.editorHint}>
                    {changedRefs
                      ? 'changed — the whole set replaces the global one'
                      : 'same as global'}
                  </span>
                </div>
                {Object.entries(references).map(([name, text]) => (
                  <div key={name} className={styles.reference}>
                    <div className={styles.referenceHead}>
                      <span>references/{name}.md</span>
                    </div>
                    <textarea
                      className={styles.editor}
                      aria-label={`Reference ${name}`}
                      value={text}
                      onChange={(e) => setReferences((r) => ({ ...r, [name]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className={styles.fields}>
          <Input
            label="Reason"
            value={reason}
            placeholder="State board wording — no 'best' claims"
            onChange={(e) => setReason(e.target.value)}
          />
          <Input
            label="Expires in (days)"
            inputMode="numeric"
            value={days}
            hint="0 = no expiry; flagged for review"
            onChange={(e) => setDays(e.target.value.replace(/[^\d]/g, ''))}
          />
        </div>
        <div className={styles.footer}>
          <Button size="sm" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Saving…' : 'Create override'}
          </Button>
          <span className={styles.spacer} />
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
